
import React, { useState } from 'react';
import { PenTool, Plus, Trash2, Info, ChevronDown, Check, Layout } from 'lucide-react';

const ACCENT_COLORS = [
  '#001F3F', '#10B981', '#2B8CEE', '#87CEEB', '#f59e0b', '#ef4444', '#ec4899', '#64748B'
];

export const BudgetPlanner = ({ categories, onAddCategory, onUpdateCategory, onDeleteCategory, onResetToStandard }: any) => {
  const [isAdding, setIsAdding] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newLimit, setNewLimit] = useState('');
  const [newColor, setNewColor] = useState(ACCENT_COLORS[0]);

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newLabel || !newLimit) return;
    onAddCategory(newLabel, parseFloat(newLimit), newColor);
    setNewLabel('');
    setNewLimit('');
    setIsAdding(false);
  };

  return (
    <div className="space-y-8 animate-fade-in pb-12">
      <div className="fin-card p-8 rounded-4xl">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-emerald/10 text-emerald rounded-2xl border border-emerald/20">
              <PenTool size={28} />
            </div>
            <div>
              <h2 className="text-2xl font-extrabold text-navy tracking-tight">Financial Architect</h2>
              <p className="text-xs font-inter text-slate-400 uppercase font-bold tracking-widest">Configure Custom Budget Stack</p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <button 
              onClick={onResetToStandard}
              className="flex items-center gap-2 px-6 py-3 border border-slate-200 hover:bg-slate-50 text-navy text-[10px] font-bold uppercase tracking-widest rounded-xl transition-all active:scale-95"
            >
              <Layout size={16} className="text-slate-400" />
              Load Template
            </button>
            <button 
              onClick={() => setIsAdding(!isAdding)}
              className="flex items-center gap-2 px-6 py-3 bg-emerald hover:bg-navy text-white text-[10px] font-inter font-bold uppercase tracking-widest rounded-xl transition-all shadow-lg shadow-emerald/20 active:scale-95"
            >
              {isAdding ? 'Cancel' : <><Plus size={16} /> Add Category</>}
            </button>
          </div>
        </div>

        {/* New Category Form */}
        {isAdding && (
          <form onSubmit={handleAdd} className="mb-10 p-6 bg-slate-50 border border-slate-100 rounded-3xl animate-fade-in">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-5 items-end">
              <div className="md:col-span-5">
                <label className="block text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-2">Category Name</label>
                <input 
                  type="text"
                  placeholder="e.g. Subscriptions"
                  value={newLabel}
                  onChange={e => setNewLabel(e.target.value)}
                  className="w-full bg-white border border-slate-200 p-4 rounded-2xl text-base text-navy font-semibold focus:border-electric outline-none transition-all"
                />
              </div>
              <div className="md:col-span-3">
                <label className="block text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-2">Monthly Limit</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-navy font-bold text-base">R</span>
                  <input 
                    type="number"
                    placeholder="0"
                    value={newLimit}
                    onChange={e => setNewLimit(e.target.value)}
                    className="w-full bg-white border border-slate-200 p-4 pl-9 rounded-2xl text-base text-navy font-bold focus:border-electric outline-none transition-all"
                  />
                </div>
              </div>
              <div className="md:col-span-3">
                <label className="block text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-2">Color Label</label>
                <div className="flex gap-2">
                  {ACCENT_COLORS.map(color => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setNewColor(color)}
                      className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${newColor === color ? 'border-navy shadow-lg' : 'border-transparent'}`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
              <div className="md:col-span-1">
                <button type="submit" className="w-full h-[52px] flex items-center justify-center bg-navy text-white rounded-2xl hover:bg-electric transition-colors shadow-lg shadow-navy/10">
                  <Check size={24} />
                </button>
              </div>
            </div>
          </form>
        )}

        {/* Categories List */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {categories.map((cat: any, index: number) => (
            <div key={cat.label} className="p-6 bg-white border border-slate-100 rounded-3xl hover:border-electric/30 transition-all duration-300 group relative">
              <div className="flex justify-between items-center mb-6">
                <div className="flex flex-col gap-3 w-full">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="relative group/color">
                        <div className="w-5 h-5 rounded-lg shadow-sm cursor-pointer hover:scale-110 transition-transform" style={{ backgroundColor: cat.color }}></div>
                        {/* Inline Color Picker for existing categories */}
                        <div className="absolute top-full left-0 mt-2 p-2 bg-white border border-slate-100 rounded-xl shadow-xl z-50 hidden group-hover/color:grid grid-cols-4 gap-1 animate-fade-in">
                          {ACCENT_COLORS.map(color => (
                            <button
                              key={color}
                              type="button"
                              onClick={() => onUpdateCategory(index, { color })}
                              className={`w-5 h-5 rounded-full border transition-transform hover:scale-110 ${cat.color === color ? 'border-navy' : 'border-transparent'}`}
                              style={{ backgroundColor: color }}
                            />
                          ))}
                        </div>
                      </div>
                      <input 
                        type="text" 
                        defaultValue={cat.label} 
                        onBlur={(e) => onUpdateCategory(index, { label: e.target.value })}
                        className="bg-transparent font-extrabold text-navy focus:outline-none focus:border-b-2 border-electric w-32 tracking-tight"
                      />
                    </div>
                    <button 
                      onClick={() => onDeleteCategory(cat.label)}
                      className="p-2 text-slate-200 hover:text-rose-500 transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="relative flex-grow">
                   <span className="absolute left-4 top-1/2 -translate-y-1/2 text-navy font-bold text-base">R</span>
                   <input 
                     type="number"
                     defaultValue={cat.limit}
                     onBlur={(e) => onUpdateCategory(index, { limit: parseFloat(e.target.value) })}
                     className="w-full bg-slate-50 border border-slate-100 p-4 pl-9 rounded-2xl text-base text-navy font-bold focus:bg-white focus:border-electric outline-none transition-all"
                   />
                </div>
                <div className="min-w-[80px] text-right">
                   <div className="text-[9px] text-slate-400 uppercase font-bold mb-1">Spent</div>
                   <div className="text-sm font-bold text-navy tracking-tight">R{cat.actual.toLocaleString()}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 p-5 bg-emerald/5 border border-emerald/10 rounded-2xl flex items-start gap-4">
          <div className="text-emerald mt-0.5"><Info size={18} /></div>
          <p className="text-[10px] font-inter text-slate-400 leading-relaxed uppercase font-bold tracking-wider">
            Your customized budget stack determines data distribution across the Home and Report screens. Changes take effect across the entire system instantly.
          </p>
        </div>
      </div>
    </div>
  );
};
